import { useQuery } from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useIsCoreMember } from '~/hooks/useIsCoreMember'
import { addProp, applyIfExist } from '~/utils'

import { useCurrentBrandAccountUserRole } from '../hooks/useCurrentBrandAccountUserRole'
import {
  canDeleteByMForce,
  canDeleteByPartnerManager,
  canDeleteByUserManager,
  canEditByMForce,
  canEditByPartnerManager,
  canEditByUserManager,
  isMForce,
  isPartnerManager,
  isUserManager,
} from '../utils'

import { useBrandAccountId } from './useBrandAccountId'
import { useMainQueryKey } from './useMainQueryKey'

const document = graphql(`
  query BrandAccountUserListPage_Main($brandAccountId: ID!) {
    brandAccount(brandAccountId: $brandAccountId) {
      id
      users {
        id
        email
        loggedInAt
        brandAccountRole {
          role {
            id
            name
          }
        }
      }
      partnerSeat {
        total
        used
      }
      userSeat {
        total
        used
      }
    }
  }
`)
export const useMainQuery = () => {
  const queryKey = useMainQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const brandAccountId = useBrandAccountId()

  const isCoreMember = useIsCoreMember()

  const currentBrandAccountUserRole =
    useCurrentBrandAccountUserRole(brandAccountId)

  return useQuery({
    queryFn: async () => {
      const result = await graphQLRequest({
        document,
        variables: { brandAccountId },
      })

      const users = R.pipe(
        result.brandAccount?.users ?? [],
        R.map(
          R.piped(
            R.evolve({
              loggedInAt: applyIfExist((raw: string) => new Date(raw)),
            }),
            addProp('role', (user) => user.brandAccountRole?.role),
            // nameが存在しないので、emailをfullNameとして使用しています
            addProp('fullName', ({ email }) => email),
            addProp('canDelete', ({ role }) => {
              const targetUserRoleName = role?.name

              if (!targetUserRoleName) {
                return false
              }

              if (isCoreMember) {
                return true
              }

              if (isMForce(currentBrandAccountUserRole.name)) {
                return canDeleteByMForce(targetUserRoleName)
              }

              if (isPartnerManager(currentBrandAccountUserRole.name)) {
                return canDeleteByPartnerManager(targetUserRoleName)
              }

              if (isUserManager(currentBrandAccountUserRole.name)) {
                return canDeleteByUserManager(targetUserRoleName)
              }

              return false
            }),
            addProp('canEdit', ({ role }) => {
              const targetUserRoleName = role?.name

              if (!targetUserRoleName) {
                return false
              }

              if (isCoreMember) {
                return true
              }

              if (isMForce(currentBrandAccountUserRole.name)) {
                return canEditByMForce(targetUserRoleName)
              }

              if (isPartnerManager(currentBrandAccountUserRole.name)) {
                return canEditByPartnerManager(targetUserRoleName)
              }

              if (isUserManager(currentBrandAccountUserRole.name)) {
                return canEditByUserManager(targetUserRoleName)
              }

              return false
            }),
          ),
        ),
      )

      return {
        users,
        partnerSeat: result.brandAccount?.partnerSeat,
        userSeat: result.brandAccount?.userSeat,
      }
    },
    queryKey,
  })
}

export type BrandAccount = NonNullable<QueryResultOf<typeof useMainQuery>>
export type BrandAccountUser = BrandAccount['users'][number]
